'use client';
import React, { useState } from 'react';

import {
  PaymentElement,
  useCheckout,
  ExpressCheckoutElement,
  BillingAddressElement,
  ShippingAddressElement
} from '@stripe/react-stripe-js/checkout';
import {
  Card,
  Stack,
  Alert,
  CardContent,
  CardHeader,
  Collapse,
  Skeleton,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Divider
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import LoadingButton from '@mui/lab/LoadingButton';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import type {
  StripeError,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent
} from '@stripe/stripe-js';
import { useUser } from '@/utils/supabase/useUser';
import { PAGE_PATHS, USER_ACCOUNT_PATHS } from '@/constants/constants';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

const schema = z.object({
  agreeToTerms: z.literal(true)
});

type PaymentFormData = z.infer<typeof schema>;

const PaymentForm = () => {
  const checkoutState = useCheckout();
  const searchParams = useSearchParams();
  const [expressOptionsReady, setExpressOptionsReady] = useState(false);

  const nextParam = searchParams.get('next') || undefined;
  console.log('Next Param in PaymentForm:', nextParam);

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    formState: { isSubmitting, errors, isValid }
  } = useForm<PaymentFormData>({
    resolver: zodResolver(schema)
  });

  if (checkoutState.type === 'loading') {
    return (
      <Stack spacing={1}>
        <Skeleton variant='rounded' height={150} />
        <Skeleton variant='text' />
        <Skeleton variant='rounded' height={75} />
      </Stack>
    );
  }

  if (checkoutState.type === 'error') {
    return <Alert severity='error'>Error: {checkoutState.error.message}</Alert>;
  }

  const checkout = checkoutState.checkout;
  const { canConfirm } = checkout;
  console.log('Can confirm payment:', canConfirm);
  console.log('Checkout state:', checkout);

  const params = new URLSearchParams();
  params.set('session_id', checkout.id);
  if (nextParam) params.set('next', nextParam);
  const returnUrl = `${window.location.origin}/signup/success?${params.toString()}`;

  console.log('Return URL for payment:', returnUrl);

  const submitHandler = handleSubmit(async () => {
    clearErrors();

    const result = await checkout.confirm({
      returnUrl
    });

    console.log('Checkout confirm result:', result);

    if (result.type === 'error') {
      console.error('Payment failed:', result.error.message);
      setError('root', { message: result.error.message });
    } else {
      console.log('Payment succeeded!');
      clearErrors();
    }
  });

  const onExpressCheckoutConfirm = (
    event: StripeExpressCheckoutElementConfirmEvent
  ) => {
    checkout.confirm({ expressCheckoutConfirmEvent: event, returnUrl });
  };

  const onExpressCheckoutReady = (
    event: StripeExpressCheckoutElementReadyEvent
  ) => {
    if (
      event.elementType === 'expressCheckout' &&
      event.availablePaymentMethods
    ) {
      setExpressOptionsReady(true);
    }
  };

  const onExpressCheckoutLoadError = (event: { error: StripeError }) => {
    console.error('Express Checkout Load Error:', event);
  };

  const isRecurring = checkout.lineItems.some((lineItem) => lineItem.recurring);

  return (
    <form onSubmit={submitHandler}>
      <Stack useFlexGap textAlign='center'>
        {/* <PaymentFormElement
          options={{ layout: 'expanded' }}
          onConfirm={onCheckoutConfirm}
        /> */}
        <ExpressCheckoutElement
          // options={{
          //   buttonType: {
          //     applePay: 'subscribe',
          //     googlePay: 'subscribe'
          //   }
          // }}
          onReady={onExpressCheckoutReady}
          onConfirm={onExpressCheckoutConfirm}
          onLoadError={onExpressCheckoutLoadError}
        />
        {expressOptionsReady && <Divider>OR</Divider>}
        {/* <BillingAddressElement /> */}
        {/* <ShippingAddressElement /> */}
        <PaymentElement
          options={{
            terms: { card: 'never', applePay: 'never', googlePay: 'never' },
            layout: 'auto'
          }}
        />
        {/* <TaxIdElement
            options={{
              visibility: 'auto',
              fields: {
                businessName: 'auto'
              }
            }}
          /> */}

        <Controller
          control={control}
          name='agreeToTerms'
          render={({ field: { onChange, value } }) => (
            <FormControlLabel
              control={
                <Checkbox checked={value || false} onChange={onChange} />
              }
              label={
                <Typography variant='body1'>
                  I have read and agree to The Urban Developer’s{' '}
                  <Link
                    target='_blank'
                    href={PAGE_PATHS.privacyPolicy}
                    prefetch={false}
                    style={{ textDecoration: 'underline', color: 'inherit' }}
                  >
                    Privacy Policy
                  </Link>{' '}
                  and{' '}
                  <Link
                    target='_blank'
                    href={PAGE_PATHS.termsAndConditions}
                    prefetch={false}
                    style={{ textDecoration: 'underline', color: 'inherit' }}
                  >
                    Terms and Conditions
                  </Link>
                </Typography>
              }
            />
          )}
        />
        <LoadingButton
          fullWidth
          variant='contained'
          type='submit'
          loading={isSubmitting}
          disabled={!isValid}
        >
          <>
            COMPLETE PAYMENT
            <br />
            {checkout.total.total.amount}
          </>
        </LoadingButton>
        {errors.root &&
          (errors.root.message ===
          'You already have a subscription with us. Manage your subscription instead.' ? (
            <Alert variant='outlined' severity='error'>
              You already have a subscription with us.{' '}
              <Link
                href={
                  USER_ACCOUNT_PATHS.account +
                  '/' +
                  USER_ACCOUNT_PATHS.manageSubscription
                }
                style={{ textDecoration: 'underline', color: 'inherit' }}
              >
                Manage your subscription instead.
              </Link>
            </Alert>
          ) : (
            <Alert variant='outlined' severity='error'>
              {errors.root.message}
            </Alert>
          ))}

        <Typography variant='caption'>
          <LockOutlinedIcon fontSize='small' sx={{ verticalAlign: 'middle' }} />{' '}
          {`Secure & encrypted payment${isRecurring ? ' • Cancel anytime' : ''}`}
        </Typography>
      </Stack>
    </form>
  );
};

const PaymentCard = () => {
  const { user } = useUser();

  return (
    <Card
      sx={(theme) => ({
        opacity: !user ? 0.6 : 1,
        border: user ? '1px solid' : null,
        borderColor: user ? theme.palette.primary.main : undefined
      })}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Typography
              variant='h2'
              sx={{
                backgroundColor: '#F4A71C',
                borderRadius: '50%',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              2
            </Typography>
            <Box>
              <Typography variant='h1'>Payment Information</Typography>
              <Typography variant='body1'>
                {!user ? 'Complete account creation first' : undefined}
              </Typography>
            </Box>
          </Box>
        }
      />
      <Collapse in={!!user}>
        <CardContent>
          <PaymentForm />
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default PaymentCard;
